import MUIDialog from '@mui/material/Dialog';
import { FunctionComponent } from 'react';

import { DialogActions } from './DialogActions';
import { DialogContent } from './DialogContent';
import { DialogProps } from './types';
import { ensureDialogCloseReason, getARIAProps } from './utils';

/**
 * Dialog component that can be used to display a dialog.
 * @param actions - the actions to display on the dialog
 * @param children - the content to display in the dialog
 * @param id - the id of the dialog
 * @param isOpen - whether the dialog is open
 * @param onClose - the callback to call when the dialog is closed via backdrop
 * click or close button click
 * @example
 * ```js
 * <Dialog isOpen={true} onClose={doSomething}>
 *   <span>Hello, world.</span>
 * </Dialog>
 * ```
 */
export const Dialog: FunctionComponent<DialogProps> = ({
  actions = [],
  children,
  id,
  isOpen = false,
  onClose,
}) => {
  const ariaProps = getARIAProps({ id });

  return (
    <MUIDialog
      onClose={(e, initialReason) => {
        if (onClose) {
          onClose(e as any, ensureDialogCloseReason(initialReason));
        }
      }}
      open={isOpen}
      {...(children ? { 'aria-describedby': ariaProps.content } : {})}
    >
      <DialogContent ariaID={ariaProps.content} containerClass="!p-5">
        {children}
      </DialogContent>

      <DialogActions actions={actions} />
    </MUIDialog>
  );
};
